import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faSyncAlt, faPlus } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/admin/ConditiiManager.css"; // ✅ CSS cu clase unice

const ConditiiManager = () => {
  // =========================
  // API endpoints (aligned)
  // =========================
  const LIST_PUBLIC_URL = "/about/conditi/";
  const CREATE_ADMIN_URL = "/about/conditii/";
  const DETAIL_ADMIN_URL = (id) => `/about/conditi/${id}/`;
  const DETAIL_PUBLIC_URL = (id) => `/about/conditii/${id}/`;

  const DESCR_URL = {
    listCreate: "/about/descriere",
    detail: (id) => `/about/descriere/${id}`,
  };

  // =========================
  // state
  // =========================
  const [conditions, setConditions] = useState([]);
  const [descriptionsByCond, setDescriptionsByCond] = useState({});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [showConditionForm, setShowConditionForm] = useState(false);
  const [showDescriptionForm, setShowDescriptionForm] = useState(false);

  const [selectedCondition, setSelectedCondition] = useState(null);
  const [selectedDescription, setSelectedDescription] = useState(null);

  const [condForm, setCondForm] = useState({ titlu: "", text: "" });
  const [descForm, setDescForm] = useState({ conditi: "", descrierea: "" });

  const canAttemptAdmin = Boolean(sessionStorage.getItem("access"));

  // =========================
  // helpers
  // =========================
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(""), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const parseApiError = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
    }
    return "A apărut o eroare.";
  };

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const extractDescrList = (detailObj) => {
    const a = detailObj?.descrierii_set;
    const b = detailObj?.descrierii;
    if (Array.isArray(a)) return a;
    if (Array.isArray(b)) return b;
    return [];
  };

  // =========================
  // fetch
  // =========================
  const fetchConditions = async () => {
    setError("");
    try {
      const res = await api.get(LIST_PUBLIC_URL);
      setConditions(normalizeList(res.data));
    } catch (err) {
      setConditions([]);
      setError(parseApiError(err));
    }
  };

  const fetchConditionDetails = async (conditionId) => {
    try {
      const res = await api.get(DETAIL_PUBLIC_URL(conditionId));
      const list = extractDescrList(res.data);
      setDescriptionsByCond((prev) => ({ ...prev, [conditionId]: list }));
    } catch {
      setDescriptionsByCond((prev) => ({ ...prev, [conditionId]: [] }));
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchConditions();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!conditions.length) return;

    conditions.forEach((c) => {
      setDescriptionsByCond((prev) => {
        if (prev[c.id] !== undefined) return prev;
        return { ...prev, [c.id]: null };
      });
      fetchConditionDetails(c.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions]);

  // =========================
  // Condition actions
  // =========================
  const openAddCondition = () => {
    setMessage("");
    setError("");
    setSelectedCondition(null);
    setCondForm({ titlu: "", text: "" });
    setShowConditionForm(true);
  };

  const openEditCondition = (condition) => {
    setMessage("");
    setError("");
    setSelectedCondition(condition);
    setCondForm({
      titlu: condition?.titlu || "",
      text: condition?.text || "",
    });
    setShowConditionForm(true);
  };

  const closeConditionForm = () => {
    if (submitting) return;
    setShowConditionForm(false);
    setSelectedCondition(null);
    setCondForm({ titlu: "", text: "" });
  };

  const submitCondition = async () => {
    setMessage("");
    setError("");

    const titlu = condForm.titlu.trim();
    const text = String(condForm.text ?? "").trim();

    if (!titlu) {
      setError("Titlul este obligatoriu.");
      return;
    }

    const payload = { titlu, text: text === "" ? null : text };

    setSubmitting(true);
    try {
      if (selectedCondition) {
        await api.put(DETAIL_ADMIN_URL(selectedCondition.id), payload);
        setMessage("Condiția a fost actualizată.");
      } else {
        await api.post(CREATE_ADMIN_URL, payload);
        setMessage("Condiția a fost adăugată.");
      }

      closeConditionForm();
      await fetchConditions();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCondition = async (id) => {
    setMessage("");
    setError("");

    const ok = window.confirm("Sigur vrei să ștergi această condiție?");
    if (!ok) return;

    setSubmitting(true);
    try {
      await api.delete(DETAIL_ADMIN_URL(id));
      setMessage("Condiția a fost ștearsă.");
      await fetchConditions();

      setDescriptionsByCond((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // Description actions
  // =========================
  const openAddDescription = () => {
    setMessage("");
    setError("");
    setSelectedDescription(null);
    setDescForm({ conditi: "", descrierea: "" });
    setShowDescriptionForm(true);
  };

  const openEditDescription = (descr) => {
    setMessage("");
    setError("");
    setSelectedDescription(descr);
    setDescForm({
      conditi: String(descr?.conditi ?? ""),
      descrierea: descr?.descrierea || "",
    });
    setShowDescriptionForm(true);
  };

  const closeDescriptionForm = () => {
    if (submitting) return;
    setShowDescriptionForm(false);
    setSelectedDescription(null);
    setDescForm({ conditi: "", descrierea: "" });
  };

  const submitDescription = async () => {
    setMessage("");
    setError("");

    const conditi = String(descForm.conditi || "").trim();
    const descrierea = String(descForm.descrierea || "").trim();

    if (!conditi) {
      setError("Selectează condiția.");
      return;
    }
    if (!descrierea) {
      setError("Descrierea este obligatorie.");
      return;
    }

    const payload = { conditi, descrierea };

    setSubmitting(true);
    try {
      if (selectedDescription) {
        await api.put(DESCR_URL.detail(selectedDescription.id), payload);
        setMessage("Descrierea a fost actualizată.");
      } else {
        await api.post(DESCR_URL.listCreate, payload);
        setMessage("Descrierea a fost adăugată.");
      }

      closeDescriptionForm();
      await fetchConditionDetails(Number(conditi));
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteDescription = async (descrId, conditionId) => {
    setMessage("");
    setError("");

    const ok = window.confirm("Sigur vrei să ștergi această descriere?");
    if (!ok) return;

    setSubmitting(true);
    try {
      await api.delete(DESCR_URL.detail(descrId));
      setMessage("Descriere ștearsă.");
      await fetchConditionDetails(conditionId);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // render
  // =========================
  const sortedConditions = useMemo(
    () => [...conditions].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
    [conditions]
  );

  if (loading) return <p className="sr-condMng__loading">Loading...</p>;

  return (
    <div className="sr-condMng">
      <div className="sr-condMng__head">
        <h2 className="sr-condMng__title">Condiții Manager</h2>
      </div>

      {message && <div className="sr-condMng__msg sr-condMng__msg--ok">{message}</div>}
      {error && <div className="sr-condMng__msg sr-condMng__msg--err">{error}</div>}

      {/* CONDITION FORM */}
      <div className="sr-condMng__block">
        {showConditionForm ? (
          <form onSubmit={(e) => e.preventDefault()} className="sr-condMng__form">
            <textarea
              className="sr-condMng__textarea"
              placeholder="Titlu"
              value={condForm.titlu}
              onChange={(e) => setCondForm((p) => ({ ...p, titlu: e.target.value }))}
              disabled={submitting}
            />
            <textarea
              className="sr-condMng__textarea"
              placeholder="Text (opțional)"
              value={condForm.text}
              onChange={(e) => setCondForm((p) => ({ ...p, text: e.target.value }))}
              disabled={submitting}
            />

            <div className="sr-condMng__actions">
              <button
                type="button"
                className="sr-condMng__btn sr-condMng__btn--primary"
                onClick={submitCondition}
                disabled={submitting}
              >
                {submitting ? "Se salvează..." : selectedCondition ? "Actualizează" : "Adaugă"}
              </button>

              <button
                type="button"
                className="sr-condMng__btn sr-condMng__btn--danger"
                onClick={closeConditionForm}
                disabled={submitting}
              >
                Close
              </button>
            </div>
          </form>
        ) : (
          <button
            className="sr-condMng__btn sr-condMng__btn--primary"
            onClick={openAddCondition}
            disabled={!canAttemptAdmin}
            type="button"
          >
            <FontAwesomeIcon icon={faPlus} /> Adaugă condiție nouă
          </button>
        )}
      </div>

      {/* DESCRIPTION FORM */}
      <div className="sr-condMng__block sr-condMng__block--spaced">
        {showDescriptionForm ? (
          <form onSubmit={(e) => e.preventDefault()} className="sr-condMng__form">
            <select
              className="sr-condMng__select"
              value={descForm.conditi}
              onChange={(e) => setDescForm((p) => ({ ...p, conditi: e.target.value }))}
              disabled={submitting}
            >
              <option value="">Selectează condiția</option>
              {sortedConditions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.titlu}
                </option>
              ))}
            </select>

            <textarea
              className="sr-condMng__textarea"
              placeholder="Descriere"
              value={descForm.descrierea}
              onChange={(e) => setDescForm((p) => ({ ...p, descrierea: e.target.value }))}
              disabled={submitting}
            />

            <div className="sr-condMng__actions">
              <button
                type="button"
                className="sr-condMng__btn sr-condMng__btn--primary"
                onClick={submitDescription}
                disabled={submitting}
              >
                {submitting ? "Se salvează..." : selectedDescription ? "Actualizează" : "Adaugă"}
              </button>

              <button
                type="button"
                className="sr-condMng__btn sr-condMng__btn--danger"
                onClick={closeDescriptionForm}
                disabled={submitting}
              >
                Close
              </button>
            </div>
          </form>
        ) : (
          <button
            className="sr-condMng__btn sr-condMng__btn--secondary"
            onClick={openAddDescription}
            disabled={!canAttemptAdmin}
            type="button"
          >
            <FontAwesomeIcon icon={faPlus} /> Adaugă descriere nouă
          </button>
        )}
      </div>

      {/* LIST */}
      <div className="sr-condMng__list">
        {sortedConditions.map((condition, index) => {
          const descrList = descriptionsByCond[condition.id];

          return (
            <div className="sr-condMng__item" key={condition.id}>
              <div className="sr-condMng__itemHead">
                <h3 className="sr-condMng__itemTitle">
                  {`${romanize(index + 1)}. ${condition.titlu}`}
                </h3>

                {canAttemptAdmin && (
                  <div className="sr-condMng__iconActions">
                    <button
                      className="sr-condMng__iconBtn sr-condMng__iconBtn--danger"
                      title="Șterge condiția"
                      onClick={() => deleteCondition(condition.id)}
                      disabled={submitting}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>

                    <button
                      className="sr-condMng__iconBtn sr-condMng__iconBtn--ok"
                      title="Editează condiția"
                      onClick={() => openEditCondition(condition)}
                      disabled={submitting}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faSyncAlt} />
                    </button>
                  </div>
                )}
              </div>

              {condition.text && <p className="sr-condMng__text">{condition.text}</p>}

              {descrList === null ? (
                <p className="sr-condMng__muted">Loading description...</p>
              ) : descrList?.length ? (
                <ol className="sr-condMng__descrList">
                  {descrList.map((d, idx) => (
                    <li className="sr-condMng__descrItem" key={d.id}>
                      <span className="sr-condMng__counter">{`${idx + 1})`}</span>
                      <span className="sr-condMng__descrText">{d.descrierea}</span>

                      {canAttemptAdmin && (
                        <span className="sr-condMng__descrActions">
                          <button
                            className="sr-condMng__iconBtn sr-condMng__iconBtn--danger"
                            title="Șterge descrierea"
                            onClick={() => deleteDescription(d.id, condition.id)}
                            disabled={submitting}
                            type="button"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>

                          <button
                            className="sr-condMng__iconBtn sr-condMng__iconBtn--ok"
                            title="Editează descrierea"
                            onClick={() => openEditDescription(d)}
                            disabled={submitting}
                            type="button"
                          >
                            <FontAwesomeIcon icon={faSyncAlt} />
                          </button>
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="sr-condMng__muted">Nu există descrieri.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConditiiManager;

function romanize(number) {
  const romanNumerals = {
    1: "I",
    2: "II",
    3: "III",
    4: "IV",
    5: "V",
    6: "VI",
    7: "VII",
    8: "VIII",
    9: "IX",
    10: "X",
  };
  return romanNumerals[number] || number.toString();
}